import { useState } from "react";
import { Box, Container, Dialog, IconButton, keyframes, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTenant } from "../../contexts/TenantContext";
import CustomButton from "../../components/CustomButton";
import useRevealOnScroll from "../../hooks/useRevealOnScroll";
import { sectionTitleSx } from "../../utils/textStyles";
import { SECTION_SCROLL_MARGIN } from "../../layouts/layoutMetrics";

const PAGE_SIZE = 9;

// The tiles do not fade in as one block the way the other sections do: each one pops a beat
// after the previous, so a page reads as a wave running down the grid. The stagger restarts
// on every page, otherwise the last tile of the third "További képek" batch would sit blank
// for over three seconds.
const TILE_DURATION_MS = 900;
const TILE_STAGGER_MS = 130;
const TILE_BASE_DELAY_MS = 240;
const TILE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

const tilePopIn = keyframes`
  from { opacity: 0; transform: scale(0.9) translate3d(0, 14px, 0); }
  to   { opacity: 1; transform: none; }
`;

// A tenant may either list bare file names under a base url, or list absolute urls and
// leave the base url empty.
const buildUrl = (baseUrl, fileName) =>
  (baseUrl ? `${baseUrl.replace(/\/+$/, "")}/${fileName}` : fileName);

const GallerySection = () => {
  const { displayConfig, homePage } = useTenant();
  const { ref, isVisible, revealSx } = useRevealOnScroll({ direction: "up", distance: 80 });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [openIndex, setOpenIndex] = useState(null);

  const gallery = homePage?.gallery;
  const images = gallery?.images ?? [];
  const visibleImages = images.slice(0, visibleCount);
  const hasMore = visibleCount < images.length;

  // Optional section: a tenant with no images gets no gallery at all.
  if (images.length === 0) {
    return null;
  }

  const closeViewer = () => setOpenIndex(null);

  const step = (delta) => setOpenIndex((current) =>
    (current + delta + visibleImages.length) % visibleImages.length);

  const handleViewerKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      step(1);
    }
    if (event.key === "ArrowLeft") {
      step(-1);
    }
  };

  // Tiles of a later page mount after the section is long past — they animate straight
  // away, and only the first page waits for the section to scroll into view.
  const tileAnimation = (index) => (isVisible
    ? `${tilePopIn} ${TILE_DURATION_MS}ms ${TILE_EASING} ` +
      `${TILE_BASE_DELAY_MS + (index % PAGE_SIZE) * TILE_STAGGER_MS}ms both`
    : "none");

  return (
    <Container maxWidth="xl">
      <Box ref={ref} id="gallery"
        sx={{
          scrollMarginTop: SECTION_SCROLL_MARGIN,
          my: { xs: 4, lg: 5, xl: 6 },
          display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* The heading carries the section reveal on its own. Wrapping the grid in it too
            would fade the tiles in as one block and swallow their stagger. */}
        <Typography variant="h4" fontWeight="bold" align="center" mb={4}
          sx={{ ...sectionTitleSx(displayConfig), ...revealSx }}>
          {gallery?.title || "Galéria"}
        </Typography>

        {/* Square tiles in a three column grid, the way an Instagram profile shows them. */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 935,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: { xs: "3px", sm: "4px" },
          }}>
          {visibleImages.map((fileName, index) => (
            <Box
              key={fileName}
              component="button"
              type="button"
              onClick={() => setOpenIndex(index)}
              aria-label={`${index + 1}. kép megnyitása teljes képernyőn`}
              sx={{
                position: "relative",
                aspectRatio: "1 / 1",
                overflow: "hidden",
                p: 0,
                border: 0,
                background: "none",
                cursor: "pointer",
                opacity: isVisible ? undefined : 0,
                animation: tileAnimation(index),
                "& img": {
                  transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                },
                "&:hover img": { transform: "scale(1.06)" },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "transparent",
                  transition: "background-color 300ms ease",
                },
                "&:hover::after": { backgroundColor: "rgba(0, 0, 0, 0.25)" },
                "&:focus-visible": {
                  outline: `2px solid ${displayConfig?.buttonColor || "#fff"}`,
                  outlineOffset: "2px",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  opacity: 1,
                  animation: "none",
                  "& img": { transition: "none" },
                },
              }}>
              <Box
                component="img"
                src={buildUrl(gallery?.thumbnailBaseUrl, fileName)}
                alt=""
                loading="lazy"
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </Box>
          ))}
        </Box>

        {hasMore && (
          <CustomButton
            variant="outlined"
            mt={3}
            text={`További képek (${visibleImages.length}/${images.length})`}
            onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
          />
        )}
      </Box>

      <Dialog
        fullScreen
        open={openIndex !== null}
        onClose={closeViewer}
        onKeyDown={handleViewerKeyDown}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.96)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}>
        {openIndex !== null && (
          <>
            <IconButton onClick={closeViewer} aria-label="Bezárás"
              sx={{ position: "fixed", top: 12, right: 12, color: "#fff" }}>
              <CloseIcon fontSize="large" />
            </IconButton>

            {visibleImages.length > 1 && (
              <IconButton onClick={() => step(-1)} aria-label="Előző kép"
                sx={{ position: "fixed", left: 8, top: "50%", transform: "translateY(-50%)", color: "#fff" }}>
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            )}

            <Box
              component="img"
              src={buildUrl(gallery?.fullSizeBaseUrl, visibleImages[openIndex])}
              alt=""
              sx={{ maxWidth: "100vw", maxHeight: "100vh", objectFit: "contain", display: "block" }}
            />

            {visibleImages.length > 1 && (
              <IconButton onClick={() => step(1)} aria-label="Következő kép"
                sx={{ position: "fixed", right: 8, top: "50%", transform: "translateY(-50%)", color: "#fff" }}>
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            )}
          </>
        )}
      </Dialog>
    </Container>
  );
}

export default GallerySection;
